import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { Tent, ArrowRight, Star } from "lucide-react";

/**
 * Direct-book partners tile — shown on the Landing page below the Trip Wall.
 *
 * Surfaces every campsite flagged `is_direct_booking: true` so partners like
 * Frowen Fields gets homepage visibility instead of being buried
 * behind the /browse filter chip. Auto-hides when there are zero partners
 * so the section never renders as an empty placeholder.
 */
export default function DirectBookPartnersTile() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/campsites/direct-book-partners");
        if (!cancelled) setItems(data?.items || []);
      } catch {
        /* silent — section auto-hides on empty */
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading || items.length === 0) return null;

  return (
    <section className="border-b hairline bg-[#fffaf6]" data-testid="direct-book-tile">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 sm:py-16">
        <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
          <div>
            <div className="eyebrow text-[#ff4500] mb-2">Book direct · no platform fees</div>
            <h2 className="font-display text-2xl sm:text-3xl tracking-tight text-[#111]">
              Direct-book partners
            </h2>
            <p className="text-sm text-[#555] mt-2 max-w-xl">
              These owners booked through CampCompass — no booking fees, no middleman,
              every penny goes to the people running the site.
            </p>
          </div>
          <Link
            to="/browse?direct_book=1"
            className="inline-flex items-center gap-1 text-sm font-display text-[#ff4500] hover:underline"
            data-testid="direct-book-tile-see-all"
          >
            See all on browse <ArrowRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.slice(0, 6).map((s) => (
            <Link
              key={s.campsite_id}
              to={`/campsites/${s.campsite_id}`}
              className="group block bg-white hairline hover:border-[#ff4500] transition-colors"
              data-testid={`direct-book-tile-${s.campsite_id}`}
            >
              <div className="aspect-[4/3] bg-[#222] overflow-hidden relative">
                {s.image ? (
                  <img
                    src={s.image}
                    alt={s.name}
                    loading="lazy"
                    onError={(e) => {
                      // iter131 — never render a black box for a broken hero image;
                      // fall back to a verified stock camping photo.
                      if (!e.currentTarget.dataset.fallback) {
                        e.currentTarget.dataset.fallback = "1";
                        e.currentTarget.src = "https://images.unsplash.com/photo-1504280390367-361c6d9f38f4?w=1200&q=80";
                      }
                    }}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-white/40">
                    <Tent className="w-12 h-12" strokeWidth={1.5} />
                  </div>
                )}
                <span className="absolute top-2 left-2 inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold uppercase tracking-wider bg-[#ff4500] text-white mono">
                  <span className="w-1.5 h-1.5 rounded-full bg-white"></span>
                  Book direct
                </span>
              </div>
              <div className="p-4">
                <div className="font-display text-base leading-tight group-hover:text-[#ff4500] line-clamp-2">
                  {s.name}
                </div>
                <div className="text-xs mono text-[#555] mt-1 truncate">{s.region}</div>
                <div className="flex items-center gap-3 mt-2 text-xs flex-wrap">
                  {typeof s.rating === "number" && s.rating > 0 && (
                    <span className="flex items-center gap-1">
                      <Star className="w-3 h-3 fill-[#ff4500] text-[#ff4500]" />
                      <span className="mono">{s.rating.toFixed(1)}</span>
                    </span>
                  )}
                  {s.open_year_round && (
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200 mono">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-500"></span> Year-round
                    </span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
